import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Car, Plus, Inbox, Building2, CreditCard, LogOut, Loader2, Trash2, Eye, EyeOff,
  Users, CheckCircle2, Clock, Phone, Mail, Calendar, Pencil, TrendingUp, Star,
} from "lucide-react";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, CATEGORIES, TRANSMISSIONS, FUELS, PLANS, unitPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PlanningTab } from "@/components/owner/PlanningTab";
import { CityPicker } from "@/components/CityPicker";

const TABS = [
  { id: "fleet", label: "Mon parc", icon: Car },
  { id: "planning", label: "Planning", icon: Calendar },
  { id: "requests", label: "Demandes", icon: Inbox },
  { id: "profile", label: "Profil agence", icon: Building2 },
  { id: "subscription", label: "Abonnement", icon: CreditCard },
];

const emptyVehicle = {
  brand: "", model: "", category: "Berline", year: 2024, plate: "", transmission: "Automatique",
  fuel: "Diesel", seats: 5, doors: 4, air_conditioning: true, price_per_day: 300, deposit: 5000,
  mileage_policy: "Kilométrage illimité", conditions: "", options: "", city: "", image_url: "", available: true,
};

export default function OwnerDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("fleet");
  const [vehicles, setVehicles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ vehicles: 0, available: 0, requests: 0, pending: 0 });
  const [cities, setCities] = useState([]);
  const [vOpen, setVOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [vForm, setVForm] = useState(emptyVehicle);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role === "admin") navigate("/admin");
    else if (user && user.role === "client") navigate("/");
  }, [user, navigate]);

  const loadAll = useCallback(() => {
    api.get("/owner/vehicles").then((r) => setVehicles(r.data));
    api.get("/owner/requests").then((r) => setRequests(r.data));
    api.get("/owner/stats").then((r) => setStats(r.data));
    api.get("/cities", { params: { scope: "all" } }).then((r) => setCities(r.data));
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  if (!user) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  const openAdd = () => { setEditing(null); setVForm({ ...emptyVehicle, city: user.city || "" }); setVOpen(true); };
  const openEdit = (v) => { setEditing(v.id); setVForm({ ...v }); setVOpen(true); };

  const saveVehicle = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...vForm,
        year: Number(vForm.year), seats: Number(vForm.seats), doors: Number(vForm.doors),
        price_per_day: Number(vForm.price_per_day), deposit: Number(vForm.deposit),
      };
      if (editing) {
        await api.put(`/owner/vehicles/${editing}`, payload);
        toast.success("Véhicule mis à jour");
      } else {
        await api.post("/owner/vehicles", payload);
        toast.success("Véhicule ajouté à votre parc");
      }
      setVOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeVehicle = async (id) => {
    await api.delete(`/owner/vehicles/${id}`);
    toast.success("Véhicule supprimé");
    loadAll();
  };

  const toggleAvail = async (id) => {
    await api.patch(`/owner/vehicles/${id}/availability`);
    loadAll();
  };

  const setReqStatus = async (id, status) => {
    await api.patch(`/owner/requests/${id}`, null, { params: { status } });
    toast.success("Statut mis à jour");
    loadAll();
  };

  const statusBadge = (s) => {
    const map = {
      pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
      accepted: { label: "Acceptée", cls: "bg-emerald-100 text-emerald-700" },
      declined: { label: "Refusée", cls: "bg-red-100 text-red-700" },
    };
    const m = map[s] || map.pending;
    return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
  };

  const vInput = (k, placeholder, type = "text") => (
    <input type={type} placeholder={placeholder} data-testid={`owner-vehicle-${k}`} value={vForm[k]} onChange={(e) => setVForm({ ...vForm, [k]: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" required />
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Espace loueur</p>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">{user.agency_name}</h1>
            <p className="text-slate-500 flex items-center gap-1.5 mt-1 text-sm">
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-white" style={{ background: "#006233" }}>
                Plan {user.plan}
              </span>
              · {user.city}
            </p>
          </div>
          <Button variant="outline" onClick={() => { logout(); navigate("/"); }} data-testid="dashboard-logout"><LogOut className="h-4 w-4 mr-1.5" /> Déconnexion</Button>
        </div>

        {/* Stats */}
        {user.status === "pending" && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="pending-banner">
            <strong>Agence en attente de validation.</strong> Votre profil et vos véhicules seront visibles du public dès que l'équipe link &amp; rent aura validé votre agence. Vous pouvez déjà préparer votre parc.
          </div>
        )}
        {user.status === "suspended" && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="suspended-banner">
            <strong>Agence suspendue.</strong> Vos véhicules ne sont plus visibles. Contactez l'équipe link &amp; rent.
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Véhicules", value: stats.vehicles, icon: Car, color: "#0B132B" },
            { label: "Disponibles", value: stats.available, icon: CheckCircle2, color: "#006233" },
            { label: "Demandes reçues", value: stats.requests, icon: Inbox, color: "#C1272D" },
            { label: "En attente", value: stats.pending, icon: Clock, color: "#D97706" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-5">
              <s.icon className="h-5 w-5 mb-3" style={{ color: s.color }} />
              <p className="font-display text-3xl font-extrabold text-slate-900">{s.value}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === t.id ? "border-emerald-700 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
              style={tab === t.id ? { borderColor: "#006233" } : {}}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* FLEET */}
        {tab === "fleet" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-xl font-bold text-slate-900">Mon parc automobile</h2>
              <Button onClick={openAdd} data-testid="owner-add-vehicle-btn" className="text-white" style={{ background: "#006233" }}>
                <Plus className="h-4 w-4 mr-1.5" /> Ajouter un véhicule
              </Button>
            </div>
            {vehicles.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 py-20 text-center text-slate-500">
                Aucun véhicule. Ajoutez votre premier véhicule pour être visible.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {vehicles.map((v) => (
                  <div key={v.id} data-testid={`owner-vehicle-card-${v.id}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                    <div className="relative aspect-[16/10] bg-slate-100">
                      <img src={v.image_url} alt={v.model} className="h-full w-full object-cover" />
                      <span className={`absolute top-3 left-3 rounded-full px-2.5 py-1 text-xs font-semibold text-white ${v.available ? "" : "bg-slate-500"}`} style={v.available ? { background: "#006233" } : {}}>
                        {v.available ? "Disponible" : "Indisponible"}
                      </span>
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-slate-900">{v.brand} {v.model}</h3>
                          <p className="text-xs text-slate-500">{v.category} · {v.city}</p>
                        </div>
                        <p className="font-display text-lg font-extrabold text-slate-900">{v.price_per_day}<span className="text-xs font-normal text-slate-400"> MAD/j</span></p>
                      </div>
                      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                        <div className="flex items-center gap-2 text-xs text-slate-600">
                          <Switch checked={v.available} onCheckedChange={() => toggleAvail(v.id)} data-testid={`owner-toggle-avail-${v.id}`} />
                          {v.available ? "En ligne" : "Masqué"}
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(v)} data-testid={`owner-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => removeVehicle(v.id)} data-testid={`owner-delete-${v.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* REQUESTS */}
        {tab === "planning" && <PlanningTab vehicles={vehicles} />}

        {tab === "requests" && (
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 mb-6">Demandes de location reçues</h2>
            {requests.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 py-20 text-center text-slate-500">Aucune demande pour le moment.</div>
            ) : (
              <div className="space-y-4">
                {requests.map((r) => (
                  <div key={r.id} data-testid={`owner-request-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="flex flex-col sm:flex-row justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-slate-900">{r.vehicle_label}</h3>
                          {statusBadge(r.status)}
                        </div>
                        <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-600">
                          <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {r.client_name}</span>
                          <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {r.client_phone}</span>
                          <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {r.client_email}</span>
                          <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {r.start_date} → {r.end_date}</span>
                        </div>
                        {r.message && <p className="mt-2 text-sm text-slate-500 italic">« {r.message} »</p>}
                      </div>
                      {r.status === "pending" && (
                        <div className="flex sm:flex-col gap-2 shrink-0">
                          <Button size="sm" className="text-white" style={{ background: "#006233" }} onClick={() => setReqStatus(r.id, "accepted")} data-testid={`req-accept-${r.id}`}>Accepter</Button>
                          <Button size="sm" variant="outline" onClick={() => setReqStatus(r.id, "declined")} data-testid={`req-decline-${r.id}`}>Refuser</Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PROFILE */}
        {tab === "profile" && <ProfileTab user={user} cities={cities} refreshUser={refreshUser} />}

        {/* SUBSCRIPTION */}
        {tab === "subscription" && <SubscriptionTab user={user} fleetSize={stats.vehicles} refreshUser={refreshUser} />}
      </div>

      {/* Vehicle dialog */}
      <Dialog open={vOpen} onOpenChange={setVOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier le véhicule" : "Ajouter un véhicule"}</DialogTitle></DialogHeader>
          <form onSubmit={saveVehicle} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {vInput("brand", "Marque")}
              {vInput("model", "Modèle")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={vForm.category} onValueChange={(v) => setVForm({ ...vForm, category: v })}>
                <SelectTrigger data-testid="owner-vehicle-category" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.filter((c) => c !== "Tous").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <CityPicker value={vForm.city} onChange={(v) => setVForm({ ...vForm, city: v })} cities={cities} testid="owner-vehicle-city" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {vInput("year", "Année", "number")}
              {vInput("plate", "Immatriculation")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={vForm.transmission} onValueChange={(v) => setVForm({ ...vForm, transmission: v })}>
                <SelectTrigger data-testid="owner-vehicle-transmission" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{TRANSMISSIONS.filter((t) => t !== "Toutes").map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={vForm.fuel} onValueChange={(v) => setVForm({ ...vForm, fuel: v })}>
                <SelectTrigger data-testid="owner-vehicle-fuel" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{FUELS.filter((f) => f !== "Tous").map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {vInput("seats", "Places", "number")}
              {vInput("doors", "Portes", "number")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {vInput("price_per_day", "Prix / jour (MAD)", "number")}
              {vInput("deposit", "Caution (MAD)", "number")}
            </div>
            <input placeholder="Politique kilométrage" value={vForm.mileage_policy} onChange={(e) => setVForm({ ...vForm, mileage_policy: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" />
            <input placeholder="Options (séparées par des virgules)" value={vForm.options} onChange={(e) => setVForm({ ...vForm, options: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" />
            <textarea placeholder="Conditions particulières" value={vForm.conditions} onChange={(e) => setVForm({ ...vForm, conditions: e.target.value })} className="w-full rounded-md border border-input px-3 py-2 text-sm" rows={2} />
            {vInput("image_url", "URL de la photo")}
            <div className="flex items-center gap-2 text-sm">
              <Switch checked={vForm.air_conditioning} onCheckedChange={(v) => setVForm({ ...vForm, air_conditioning: v })} /> Climatisation
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="w-full text-white" style={{ background: "#006233" }} data-testid="owner-vehicle-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Enregistrer" : "Ajouter au parc"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfileTab({ user, cities, refreshUser }) {
  const [form, setForm] = useState({
    agency_name: user.agency_name || "", owner_name: user.owner_name || "", phone: user.phone || "",
    whatsapp: user.whatsapp || "", city: user.city || "", address: user.address || "",
    ice: user.ice || "", rc: user.rc || "",
    description: user.description || "", logo_url: user.logo_url || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/owner/profile", form);
      await refreshUser();
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-xl font-bold text-slate-900 mb-6">Profil commercial de l'agence</h2>
      <form onSubmit={save} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Nom de l'agence</label><input value={form.agency_name} data-testid="profile-agency-name" onChange={(e) => setForm({ ...form, agency_name: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" /></div>
          <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Responsable</label><input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" /></div>
          <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Téléphone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" /></div>
          <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">WhatsApp</label><input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" /></div>
          <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">ICE</label><input data-testid="profile-ice" value={form.ice} onChange={(e) => setForm({ ...form, ice: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" /></div>
          <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Registre de Commerce (RC)</label><input data-testid="profile-rc" value={form.rc} onChange={(e) => setForm({ ...form, rc: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" /></div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ville</label>
            <CityPicker value={form.city} onChange={(v) => setForm({ ...form, city: v })} cities={cities} testid="profile-city" />
          </div>
          <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Adresse</label><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-10 w-full rounded-md border border-input px-3 text-sm" /></div>
        </div>
        <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Présentation</label><textarea value={form.description} data-testid="profile-description" onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-md border border-input px-3 py-2 text-sm" rows={4} /></div>
        <Button type="submit" disabled={saving} className="text-white" style={{ background: "#006233" }} data-testid="profile-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le profil"}
        </Button>
      </form>
    </div>
  );
}

function SubscriptionTab({ user, fleetSize, refreshUser }) {
  const [saving, setSaving] = useState(false);
  const size = Math.max(fleetSize, 1);

  const choose = async (planKey) => {
    setSaving(true);
    try {
      await api.put("/owner/profile", { plan: planKey });
      await refreshUser();
      toast.success(`Plan ${planKey} activé (démo)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="font-display text-xl font-bold text-slate-900 mb-1">Mon abonnement</h2>
      <p className="text-slate-500 text-sm mb-6">Votre parc compte <strong>{size}</strong> véhicule(s). Prix calculés automatiquement (démonstration, sans paiement réel).</p>
      <div className="grid md:grid-cols-3 gap-5">
        {PLANS.map((plan) => {
          const unit = unitPrice(plan.key, size);
          const active = user.plan === plan.key;
          return (
            <div key={plan.key} data-testid={`sub-plan-${plan.key}`} className={`rounded-3xl border bg-white p-6 ${active ? "ring-2" : "border-slate-200"}`} style={active ? { ringColor: plan.color, borderColor: "transparent" } : {}}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-extrabold" style={{ color: plan.color }}>{plan.name}</h3>
                {active && <span className="rounded-full px-2 py-0.5 text-xs font-semibold text-white" style={{ background: plan.color }}>Actif</span>}
              </div>
              <p className="text-xs text-slate-500 italic mt-1">« {plan.tagline} »</p>
              <div className="mt-4">
                <span className="font-display text-3xl font-extrabold text-slate-900">{unit}</span>
                <span className="text-slate-500 text-sm"> MAD/véh./mois</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">Total : <strong className="text-slate-900">{(unit * size).toLocaleString()} MAD/mois</strong></p>
              <Button disabled={active || saving} onClick={() => choose(plan.key)} className="w-full mt-5 text-white" style={{ background: active ? "#94a3b8" : plan.color }} data-testid={`sub-choose-${plan.key}`}>
                {active ? "Plan actuel" : `Passer en ${plan.name}`}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
